import { pushSubscriptionsPathTo } from './Api';

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function isStandalonePwa() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

export function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || '');
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function pushServiceWorkerUrl() {
  const publicUrl = process.env.PUBLIC_URL || '';
  const script = process.env.NODE_ENV === 'production'
    ? '/service-worker.js'
    : '/dev-push-sw.js';
  return `${publicUrl}${script}`;
}

async function getPushRegistration() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('no-sw');
  }
  return navigator.serviceWorker.register(pushServiceWorkerUrl(), { updateViaCache: 'none' });
}

export async function getCurrentSubscription() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return null;
  }
  try {
    const registration = await getPushRegistration();
    if (!registration?.pushManager) {
      return null;
    }
    return registration.pushManager.getSubscription();
  } catch (error) {
    console.warn('Could not read push subscription', error);
    return null;
  }
}

export async function isThisDeviceSubscribed() {
  const subscription = await getCurrentSubscription();
  return Boolean(subscription);
}

async function persistSubscription(subscription) {
  const json = subscription.toJSON();
  await pushSubscriptionsPathTo.create({
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    user_agent: navigator.userAgent,
  });
}

export async function subscribeThisDevice() {
  const registration = await getPushRegistration();
  if (!registration?.pushManager) {
    throw new Error('no-sw');
  }

  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    await persistSubscription(existing);
    return existing;
  }

  const { data } = await pushSubscriptionsPathTo.vapid();
  if (!data?.public_key) {
    throw new Error('no-vapid');
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.public_key),
  });
  await persistSubscription(subscription);
  return subscription;
}

export async function unsubscribeThisDevice() {
  const subscription = await getCurrentSubscription();
  if (!subscription) {
    return;
  }

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  try {
    await pushSubscriptionsPathTo.destroy({ endpoint });
  } catch (error) {
    console.warn('Could not delete push subscription on the server', error);
  }
}
